import api from "api/axios";
import type { ColorScheme } from "../ui/placeholders";

export interface PromptCategory {
  id: number;
  name: string;
  description: string | null;
  created_at: string | null;
  updated_at: string | null;
  prompt_count?: number;
}

export interface Prompt {
  id: number;
  category_id: number | null;
  title: string;
  body: string;
  tags: string[] | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CategoryCreate {
  name: string;
  description?: string;
}

export interface CategoryUpdate {
  name?: string;
  description?: string;
}

export interface PromptCreate {
  category_id?: number | null;
  title: string;
  body: string;
  tags?: string[];
}

export interface PromptUpdate {
  category_id?: number | null;
  title?: string;
  body?: string;
  tags?: string[];
}

export interface PromptFilters {
  category_id?: number;
  search?: string;
}

function promptLibraryApiBase(): string {
  return localStorage.getItem("authUserType") === "admin"
    ? "/admin/prompt-library"
    : "/employee/prompt-library";
}

// Categories
export async function listCategories(): Promise<PromptCategory[]> {
  const res = await api.get(`${promptLibraryApiBase()}/categories`);
  return res.data;
}

export async function createCategory(data: CategoryCreate): Promise<PromptCategory> {
  const res = await api.post(`${promptLibraryApiBase()}/categories`, data);
  return res.data;
}

export async function updateCategory(id: number, data: CategoryUpdate): Promise<PromptCategory> {
  const res = await api.patch(`${promptLibraryApiBase()}/categories/${id}`, data);
  return res.data;
}

export async function deleteCategory(id: number): Promise<void> {
  await api.delete(`${promptLibraryApiBase()}/categories/${id}`);
}

// Prompts
export async function listPrompts(filters: PromptFilters = {}): Promise<Prompt[]> {
  const params = new URLSearchParams();
  if (filters.category_id !== undefined) params.set("category_id", String(filters.category_id));
  if (filters.search) params.set("search", filters.search);
  const res = await api.get(`${promptLibraryApiBase()}/prompts?${params.toString()}`);
  return res.data;
}

export async function createPrompt(data: PromptCreate): Promise<Prompt> {
  const res = await api.post(`${promptLibraryApiBase()}/prompts`, data);
  return res.data;
}

export async function updatePrompt(id: number, data: PromptUpdate): Promise<Prompt> {
  const res = await api.patch(`${promptLibraryApiBase()}/prompts/${id}`, data);
  return res.data;
}

export async function deletePrompt(id: number): Promise<void> {
  await api.delete(`${promptLibraryApiBase()}/prompts/${id}`);
}

// Predefined placeholder data
export async function getMyColorScheme(): Promise<ColorScheme> {
  const isAdmin = localStorage.getItem("authUserType") === "admin";
  const res = await api.get(isAdmin ? "/admin/company_profile" : "/employee/profile");
  const cs = res.data.color_scheme || {};
  return {
    primaryColor: cs.primary_color,
    secondaryColor: cs.secondary_color,
    accentColor: cs.accent_color,
    backgroundColor: cs.background_color,
  };
}
